import type { MeetingStatus } from "@performyard/models";

/**
 * Configuration constants for the Meeting Details page
 */
export const config = {
	/** Maximum length for meeting description text */
	meetingDescriptionMaxLength: 2000,

	/** Height at which description text should collapse with "Read More" */
	descriptionCollapseHeight: 48,

	/** Height at which past meeting notes should collapse with "Read More" */
	pastMeetingNotesCollapseHeight: 80,

	/** Autosave delay for notes in milliseconds */
	autosaveDelay: 1500,

	/** Minimum time to show "Saving" state in milliseconds */
	minSavingTime: 1000,

	/** Maximum number of past meetings to show in the accordion */
	pastMeetingsLimit: 5,

	/** Layout dimensions */
	layout: {
		leftColumnWidth: 848,
		rightColumnWidth: 480,
		headerIconSize: 32,
		avatarSize: 28,
		smallAvatarSize: 24,
		dropdownAvatarSize: 20,
		dropdownMenuWidth: 200,
		checklistInputMinRows: 1,
		notesMinRows: 11,
		notesMaxRows: 11,
		privateNotesMinRows: 11,
		privateNotesMaxRows: 11,
		pastMeetingNotesMinRows: 4,
		pastMeetingNotesMaxRows: 8,
		accordionMaxHeight: 402,
		breadcrumbMaxWidth: 146,
		buttonSize: 24,
		calendarIconContainerSize: 24,
	},
	layoutClasses: {
		pageContainer: "flex h-[calc(100vh-64px)]",
		leftColumn: "min-h-0 w-2/3 overflow-y-auto pb-6 pt-4",
		leftColumnInner: "flex min-h-0 flex-col gap-6 px-12",
		rightColumn:
			"flex h-full w-1/3 flex-shrink-0 flex-col border-l border-t-0 border-solid border-neutral-2 bg-white",
		optionsDropdown: "w-[180px] flex-shrink-0",
	},
	goals: {
		maxHeight: 380,
		listLimit: 25,
	},
	assignment: {
		defaultDueTime: {
			hours: 17,
			minutes: 0,
			seconds: 0,
			milliseconds: 0,
		},
		dueTimeLabel: "5:00pm",
	},

	/** Icon sizes */
	iconSize: {
		small: 12,
		medium: 16,
		large: 20,
		xl: 32,
	},

	/** Spinner sizes */
	spinnerSize: {
		small: 20,
		medium: 32,
	},

	/** Z-index values for layered elements */
	zIndex: {
		dropdown: 1000,
		notesDropdown: 2010,
		pastMeetingDropdown: 10,
	},

	/** Floating dropdown offset distances */
	dropdownOffset: {
		default: 8,
		notes: 5,
	},

	/** Available meeting status options */
	statusOptions: ["active", "completed", "canceled", "archived"] as const,
} as const;

/**
 * Status chip configuration for displaying meeting status
 */
export const MEETING_STATUS_CONFIG: Record<
	MeetingStatus,
	{ label: string; color: "green" | "primary" | "red" | "neutral" }
> = {
	active: { label: "Active", color: "primary" },
	completed: { label: "Completed", color: "green" },
	canceled: { label: "Canceled", color: "red" },
	archived: { label: "Archived", color: "neutral" },
};

/**
 * Configuration for status change confirmation modals
 */
export const STATUS_CONFIRMATION_CONFIG: Record<
	"canceled" | "completed" | "archived",
	{ title: string; message: string; confirmText: string; confirmButtonColor: "green" | "red" }
> = {
	canceled: {
		title: "Cancel Meeting?",
		message:
			"All meeting notes, agenda, and action items will be placed in read-only mode and no longer configurable.",
		confirmText: "Yes, Cancel Meeting",
		confirmButtonColor: "green",
	},
	completed: {
		title: "Complete Meeting?",
		message: "Are you sure you want to complete this meeting?",
		confirmText: "Yes, Complete Meeting",
		confirmButtonColor: "green",
	},
	archived: {
		title: "Archive Meeting?",
		message:
			"All meeting notes, agenda, and action items will be placed in read-only mode and no longer configurable.",
		confirmText: "Yes, Archive Meeting",
		confirmButtonColor: "green",
	},
};

/**
 * Configuration for remove from calendar confirmation modal
 */
export const REMOVE_CALENDAR_CONFIG = {
	title: "Remove Meeting From Calendar?",
	message:
		"Do you want to remove this meeting from your calendar? This will delete the event, but you can always add a new one later.",
	confirmText: "Yes, Remove",
	confirmButtonColor: "red" as const,
};
